// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: ClickUp card > Manage. Swaps the connections grid like the Teams groups
// manager. Holds "Counts as done in Konvo": one switch per ClickUp status that
// is still open in ClickUp (busiest first). A status that is on counts as
// finished for this person in Konvo (no overdue, out of open counts, shown with
// Closed), matched by name on every list. ClickUp itself is not changed, so
// colleagues who still act on it (e.g. invoicing) keep seeing it open.

import manager from '@app/features/konvo_connections/components/KonvoClickUpManager.module.css';
import styles from '@app/features/konvo_connections/components/KonvoConnectorCard.module.css';
import ui from '@app/features/konvo_connections/components/KonvoTeamsGroupsCard.module.css';
import KonvoClickUpDoneStatuses, {
	type DoneStatusOption,
} from '@app/features/konvo_tasks/state/KonvoClickUpDoneStatuses';
import {Button} from '@app/features/ui/button/Button';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {Switch} from '@app/features/ui/components/form/FormSwitch';
import {msg} from '@lingui/core/macro';
import {Trans, useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import {useEffect} from 'react';

const TITLE_DESCRIPTOR = msg({
	message: 'ClickUp',
	comment: 'Title of the ClickUp manager opened from the ClickUp card in Settings > Connections.',
});
const COUNTS_DESCRIPTOR = msg({
	message: '{open, plural, one {# open task} other {# open tasks}} in ClickUp, {mine} yours',
	comment:
		'ClickUp manager > Counts as done in Konvo: line under a ClickUp status. {open} is how many tasks are in that status in ClickUp, {mine} how many of them are assigned to you. Example: "12 open tasks in ClickUp, 3 yours".',
});
const NONE_NOW_DESCRIPTOR = msg({
	message: 'No open tasks in this status right now',
	comment:
		'ClickUp manager > Counts as done in Konvo: line under a chosen status that no open task uses at the moment.',
});
const SAVE_FAILED_DESCRIPTOR = msg({
	message: "Couldn't save that. Try again.",
	comment: 'Toast when turning a ClickUp status on or off under Counts as done in Konvo fails.',
});
const COUNTS_AS_DONE_DESCRIPTOR = msg({
	message: 'Count “{status}” as done in Konvo',
	comment:
		'Accessible label of the switch on a ClickUp status in the ClickUp manager. {status} is the status name, e.g. "to be invoiced".',
});

const StatusRow = observer(({option}: {option: DoneStatusOption}) => {
	const {i18n} = useLingui();
	const on = KonvoClickUpDoneStatuses.isSelected(option.status);
	const toggle = (value: boolean) => {
		void KonvoClickUpDoneStatuses.toggle(option.status, value).then((ok) => {
			if (!ok) ToastCommands.error(i18n._(SAVE_FAILED_DESCRIPTOR));
		});
	};
	return (
		<div className={ui.row} data-flx="konvo-connections.clickup-manager.status">
			<span className={manager.dot} style={{backgroundColor: option.color ?? undefined}} aria-hidden="true" />
			<div className={ui.rowInfo}>
				<div className={`${styles.name} ${manager.status}`}>{option.status}</div>
				<div className={styles.account}>
					{option.open_count > 0
						? i18n._(COUNTS_DESCRIPTOR, {open: option.open_count, mine: option.mine_count})
						: i18n._(NONE_NOW_DESCRIPTOR)}
				</div>
			</div>
			<div className={ui.rowActions}>
				<Switch
					compact
					value={on}
					onChange={toggle}
					ariaLabel={i18n._(COUNTS_AS_DONE_DESCRIPTOR, {status: option.status})}
					data-flx="konvo-connections.clickup-manager.switch"
				/>
			</div>
		</div>
	);
});

export const KonvoClickUpManager = observer(({onBack}: {onBack: () => void}) => {
	const {i18n} = useLingui();
	useEffect(() => {
		void KonvoClickUpDoneStatuses.load();
	}, []);
	const store = KonvoClickUpDoneStatuses;
	return (
		<section className={ui.manager} aria-label={i18n._(TITLE_DESCRIPTOR)} data-flx="konvo-connections.clickup-manager">
			<div className={ui.header}>
				<Button variant="ghost" small fitContent onClick={onBack}>
					<Trans>← Connections</Trans>
				</Button>
				<h2 className={styles.name}>{i18n._(TITLE_DESCRIPTOR)}</h2>
				<div className={manager.section}>
					<h3 className={manager.sectionTitle}>
						<Trans>Counts as done in Konvo</Trans>
					</h3>
					<p className={styles.description}>
						<Trans>
							Tasks in these ClickUp statuses count as finished for you in Konvo: they're no longer overdue, they leave
							your open counts and they show with Closed. ClickUp doesn't change, and nobody else's Konvo changes.
						</Trans>
					</p>
				</div>
			</div>
			<div className={ui.list} aria-busy={store.loading} data-flx="konvo-connections.clickup-manager.statuses">
				{store.options.length === 0 ? (
					<p className={ui.empty} role={store.error ? 'alert' : 'status'}>
						{store.loading ? (
							<Trans>Loading your ClickUp statuses…</Trans>
						) : store.error ? (
							<Trans>Couldn't load your ClickUp statuses.</Trans>
						) : (
							<Trans>There are no open ClickUp statuses to choose from yet.</Trans>
						)}
					</p>
				) : (
					store.options.map((option) => <StatusRow key={option.status} option={option} />)
				)}
			</div>
		</section>
	);
});
